import { FilePreviewComponent } from '@/components';
import { DataTableColumnType, FilterType, RateListingPreset } from '@/enums';
import { DataTableColumn, Rate } from '@/interfaces';

export function buildRateColumns(
    preset: RateListingPreset,
    ctx: {
        hasDistrictMapping: boolean;
        hasCompetitor: boolean;
        autocomplete: (key: any, q: string, context?: Record<string, any>) => any;
        rateUnits$: (context?: Record<string, any>) => any;
        years$: (context?: Record<string, any>) => any;
        docaVersions$: (context?: Record<string, any>) => any;
        navigate: (agency: string, district_name_mapped: string) => any;
    }
): DataTableColumn<Rate>[] {
    const districtNameColumn = (
        showIf?: () => boolean
    ): DataTableColumn<Rate> => ({
        field: 'district_name_mapped',
        header: 'District Name',
        type: DataTableColumnType.ELLIPSIS,
        filterType: FilterType.AUTOCOMPLETE,
        ellipsisTooltip: (row) =>
            row.district_name !== row.district_name_mapped
                ? `Original District Name: ${row.district_name}`
                : row.district_name_mapped,
        autocompleteCompleteMethod: (q, context) => ctx.autocomplete('district_names', q, context),
        ...(showIf ? { showIf } : {})
    });

    const base: DataTableColumn<Rate>[] = [
        {
            ...districtNameColumn(() => !ctx.hasDistrictMapping)
        },
        {
            field: 'county',
            header: 'County',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('counties', q, context),
            showIf: () => !ctx.hasDistrictMapping
        },
        {
            field: 'state',
            header: 'State',
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('states', q, context),
            showIf: () => !ctx.hasDistrictMapping
        },
        {
            field: 'agency_name_mapped',
            header: 'Agency Name',
            filterType: FilterType.AUTOCOMPLETE,
            filterField: 'display_agency_name',
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('agency_names', q, context),
            type: DataTableColumnType.LINK,
            formatter: (row: Rate) => (row.competitor_id ? row.agency_name_mapped : row.provider_name),
            link: (row: Rate) => (row.competitor_id ? `/competitor-mappings/${row.competitor_id}` : null),
            showIf: () => !ctx.hasCompetitor
        },
        {
            field: 'provider_type',
            header: 'Agency Type',
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('provider_types', q, context),
            showIf: () => !ctx.hasCompetitor
        },
        {
            field: 'service_name_mapped',
            header: 'Service (Normalized)',
            type: DataTableColumnType.ELLIPSIS,
            filterType: FilterType.AUTOCOMPLETE,
            ellipsisTooltip: (row) => (row.service_name !== row.service_name_mapped ? `Original Service Name: ${row.service_name}` : row.service_name_mapped),
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('services', q, context)
        },
        {
            field: 'rate_min',
            header: 'Rate (Min)',
            formatter: (row: Rate) => '$' + row.rate_min,
            filterType: FilterType.NUMERIC,
            filterPlaceholder: 'From'
        },
        {
            field: 'pqg_rate_min',
            header: 'PQG (Min)',
            formatter: (row: Rate) => (row.pqg_rate_min ? '$' + row.pqg_rate_min : 'N/A'),
            filterType: FilterType.NUMERIC,
            filterPlaceholder: 'From',
            cellClass: 'bg-sky-100 dark:bg-sky-950/40',
            headerClass: 'bg-sky-100 dark:bg-sky-950/40'
        },
        {
            field: 'rate_max',
            header: 'Rate (Max)',
            formatter: (row: Rate) => '$' + row.rate_max,
            filterType: FilterType.NUMERIC,
            filterPlaceholder: 'From'
        },
        {
            field: 'pqg_rate_max',
            header: 'PQG (Max)',
            formatter: (row: Rate) => (row.pqg_rate_max ? '$' + row.pqg_rate_max : 'N/A'),
            filterType: FilterType.NUMERIC,
            filterPlaceholder: 'From',
            cellClass: 'bg-sky-100 dark:bg-sky-950/40',
            headerClass: 'bg-sky-100 dark:bg-sky-950/40'
        },
        {
            field: 'rate_unit_mapped',
            header: 'Rate Unit',
            filterType: FilterType.SELECT,
            filterOptionsFn: ctx.rateUnits$
        },
        {
            field: 'year',
            header: 'School Year',
            filterType: FilterType.SELECT,
            filterOptionsFn: ctx.years$
        },
        {
            field: 'created_at',
            header: 'Document Retrieved Date',
            type: DataTableColumnType.DATE,
            filterType: FilterType.DATE
        },
        {
            field: 'source_file',
            header: 'Document',
            type: DataTableColumnType.MODAL,
            modalComponent: FilePreviewComponent,
            modalTitle: 'View Document',
            modalData: (row: Rate) => ({ url: row.source_file_url }),
            modalBtnDisabled: (row: Rate) => !row.source_file_url
        },
        {
            field: 'notes',
            header: 'Notes',
            type: DataTableColumnType.WRAP
        },
        {
            field: 'doca_version',
            header: 'Analyzer Version',
            filterType: FilterType.SELECT,
            filterOptionsFn: ctx.docaVersions$
        }
    ];

    const districtColumns: DataTableColumn<Rate>[] = [
        districtNameColumn(),
        {
            field: 'county',
            header: 'County',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('counties', q, context)
        },
        {
            field: 'state',
            header: 'State',
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('states', q, context)
        }
    ];

    const documentColumns: DataTableColumn<Rate>[] = [
        {
            field: 'source_file',
            header: 'Document',
            type: DataTableColumnType.MODAL,
            modalComponent: FilePreviewComponent,
            modalTitle: 'View Document',
            modalData: (row: Rate) => ({ url: row.source_file_url }),
            modalBtnDisabled: (row: Rate) => !row.source_file_url
        },
        districtNameColumn(),
        {
            field: 'created_at',
            header: 'Document Retrieved Date',
            type: DataTableColumnType.DATE,
            filterType: FilterType.DATE
        }
    ];

    const competitorColumns: DataTableColumn<Rate>[] = [
        {
            field: 'agency_name_mapped',
            header: 'Agency Name',
            filterType: FilterType.AUTOCOMPLETE,
            filterField: 'display_agency_name',
            type: DataTableColumnType.LINK,
            link: (row: Rate) => (row.competitor_id ? `/competitor-mappings/${row.competitor_id}` : null),
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('agency_names', q, context)
        },
        {
            field: 'agency_type_mapped',
            header: 'Agency Type',
            filterType: FilterType.AUTOCOMPLETE,
            matchMode: 'in',
            autocompleteCompleteMethod: (q, context) => ctx.autocomplete('agency_types', q, context)
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-eye',
                    label: 'View Rates',
                    command: (row: Rate) => ctx.navigate(row.agency_name_mapped, row.district_name_mapped)
                }
            ]
        }
    ];

    switch (preset) {
        case RateListingPreset.BY_DISTRICT:
            return districtColumns;
        case RateListingPreset.BY_SOURCE_FILE:
            return documentColumns;
        case RateListingPreset.BY_COMPETITOR:
            return competitorColumns;
        default:
            return base;
    }
}
